import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Phone, PhoneCall, Plus, Users, Filter, ChevronRight, Loader2, X,
  Trash2, CheckCircle2, PlayCircle, Search, User, Building2, Landmark,
  History, BarChart3, ClipboardList, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

const TYPE_LABEL = { individual: "Individual / Farmer", business: "Business", government: "Government" };
const TYPE_ICON = { individual: User, business: Building2, government: Landmark };
const TYPE_DOT  = { individual: "bg-green-500", business: "bg-amber-500", government: "bg-red-500" };

function StatCard({ label, value, sub, tint = "bg-[#121815]" }) {
  return (
    <div className={`rounded-md border border-[#3A4640] p-4 ${tint}`}>
      <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98]">{label}</div>
      <div className="mt-1 text-2xl font-heading font-bold text-[#F8F9FA]">{value}</div>
      {sub && <div className="text-xs text-[#8A8F98] mt-0.5">{sub}</div>}
    </div>
  );
}

export default function Engagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [states, setStates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [locationTree, setLocationTree] = useState([]);
  const [historyCampaign, setHistoryCampaign] = useState(null);
  const [resultsCampaign, setResultsCampaign] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: camps }, meta, prodMeta, tree] = await Promise.all([
        api.get("/calling/campaigns"),
        api.get("/customers/meta").catch(() => ({ data: { districts: [], states: [] } })),
        api.get("/products/master/meta").catch(() => ({ data: { categories: [], brands: [] } })),
        api.get("/customers/location-tree").catch(() => ({ data: { districts: [] } })),
      ]);
      setCampaigns(camps || []);
      setDistricts(meta.data?.districts || []);
      setStates(meta.data?.states || []);
      setLocationTree(tree.data?.districts || []);
      setCategories([...(prodMeta.data?.categories || [])].sort());
      setBrands([...(prodMeta.data?.brands || [])].sort());
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load engagement data");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const deleteCampaign = async (id) => {
    try {
      await api.delete(`/calling/campaigns/${id}`);
      toast.success("Campaign removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  const active = campaigns.filter((c) => c.status === "active");
  const done = campaigns.filter((c) => c.status === "completed");

  return (
    <div className="p-6 md:p-8" data-testid="engagement-page">
      <PageHeader
        title="Customer Engagement"
        subtitle="Proactively call customers, capture outcomes, and never lose an interaction."
        action={
          <Button className="bg-[#39FF14] hover:bg-[#2ECC71]" onClick={() => setNewOpen(true)} data-testid="new-calling-campaign-btn">
            <Plus size={16} className="mr-2" /> New Calling Campaign
          </Button>
        }
      />

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <StatCard label="Active Campaigns" value={active.length} tint="bg-[#121815]" />
        <StatCard label="Completed" value={done.length} sub="All-time" tint="bg-[#121815]" />
        <StatCard label="Interactions logged" value={campaigns.reduce((n, c) => n + (c.done_ids?.length || 0), 0)} sub="Across all campaigns" tint="bg-[#121815]" />
      </div>

      <div className="bg-[#121815] border border-[#3A4640] rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-[#3A4640] flex items-center justify-between">
          <div className="text-sm font-heading font-semibold text-[#F8F9FA] uppercase tracking-wider">Your Campaigns</div>
          {loading && <Loader2 size={14} className="text-[#8A8F98] animate-spin" />}
        </div>
        {!loading && campaigns.length === 0 && (
          <div className="px-5 py-12 text-center" data-testid="engagement-empty">
            <PhoneCall size={32} className="mx-auto text-[#8A8F98]" />
            <div className="mt-3 text-sm text-[#8A8F98]">No campaigns yet.</div>
            <div className="text-xs text-[#8A8F98] mt-1">Create a filtered calling list to get started.</div>
            <Button className="mt-4 bg-[#39FF14] hover:bg-[#2ECC71]" onClick={() => setNewOpen(true)} data-testid="create-first-campaign-btn">
              <Plus size={14} className="mr-2" /> Create Calling Campaign
            </Button>
          </div>
        )}
        {campaigns.map((c) => {
          const processed = (c.done_ids?.length || 0) + (c.skipped_ids?.length || 0);
          const pct = c.total ? Math.round((processed / c.total) * 100) : 0;
          const isActive = c.status === "active";
          return (
            <div key={c.id} className="px-5 py-4 border-b border-[#3A4640] last:border-0 hover:bg-[#0B0F0C] transition-colors" data-testid={`campaign-row-${c.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading font-semibold text-[#F8F9FA]">{c.name}</div>
                    <span className={`text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border ${isActive ? "bg-emerald-100 text-[#39FF14] border-emerald-200" : "bg-[#1A1F1C] text-[#F8F9FA] border-[#3A4640]"}`}>
                      {isActive ? "Active" : "Completed"}
                    </span>
                  </div>
                  <div className="text-xs text-[#8A8F98] mt-1">
                    Created {formatDate(c.created_at)} by {c.created_by_name || "—"} · {c.total} customers
                  </div>
                  <div className="mt-2 flex items-center gap-3">
                    <div className="w-56 h-1.5 bg-[#1A1F1C] rounded-full overflow-hidden">
                      <div className="h-full bg-[#39FF14] rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-[11px] text-[#8A8F98]">{processed} / {c.total} ({pct}%)</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isActive && (
                    <Button size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-[#0B0F0C]" onClick={() => navigate(`/engagement/campaigns/${c.id}`)} data-testid={`resume-campaign-${c.id}`}>
                      <PlayCircle size={14} className="mr-1.5" /> {processed > 0 ? "Resume" : "Start"}
                    </Button>
                  )}
                  {!isActive && (
                    <Button variant="outline" size="sm" onClick={() => navigate(`/engagement/campaigns/${c.id}`)} data-testid={`view-campaign-${c.id}`}>
                      View
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setHistoryCampaign(c)} data-testid={`history-campaign-${c.id}`} title="Campaign history">
                    <History size={14} />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setResultsCampaign(c)} data-testid={`results-campaign-${c.id}`} title="Campaign results">
                    <BarChart3 size={14} />
                  </Button>
                  <button onClick={() => deleteCampaign(c.id)} className="p-1.5 text-[#8A8F98] hover:text-red-600" title="Delete campaign" data-testid={`delete-campaign-${c.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <CampaignBuilder
        open={newOpen}
        onClose={() => setNewOpen(false)}
        districts={districts}
        states={states}
        categories={categories}
        brands={brands}
        locationTree={locationTree}
        onCreated={(id) => { setNewOpen(false); navigate(`/engagement/campaigns/${id}`); }}
      />
      <CampaignHistoryModal campaign={historyCampaign} onClose={() => setHistoryCampaign(null)} />
      <CampaignResultsModal campaign={resultsCampaign} onClose={() => setResultsCampaign(null)} />
    </div>
  );
}

// -------------------- Campaign History Modal --------------------
const RESULT_LABEL = {
  working_perfectly: "Working perfectly", working_with_issues: "Working with issues",
  not_working: "Not working", under_repair: "Under repair", sold_or_disposed: "Sold / Disposed",
  not_assessed: "Not assessed", none: "None",
  preventive_service: "Preventive service", minor_repair: "Minor repair",
  major_repair: "Major repair", parts_replacement: "Parts replacement",
  not_interested: "Not interested", just_browsing: "Just browsing",
  considering: "Considering", actively_looking: "Actively looking", ready_to_buy: "Ready to buy",
  not_planning: "Not planning", within_month: "Within a month",
  "1_to_3_months": "1–3 months", "3_to_6_months": "3–6 months",
  beyond_6_months: "Beyond 6 months", undecided: "Undecided",
};
const _lbl = (k) => RESULT_LABEL[k] || k;

function CampaignHistoryModal({ campaign, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!campaign) { setData(null); return; }
    setLoading(true);
    api.get(`/calling/campaigns/${campaign.id}/history`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load history"))
      .finally(() => setLoading(false));
  }, [campaign]);

  return (
    <Dialog open={!!campaign} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="campaign-history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History size={16} /> Campaign History</DialogTitle>
          <DialogDescription>{campaign?.name}</DialogDescription>
        </DialogHeader>
        {loading && <div className="p-6 text-center"><Loader2 size={20} className="animate-spin mx-auto text-[#8A8F98]" /></div>}
        {data && (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-[#0B0F0C] border border-[#3A4640] rounded">
                <div className="text-[10px] uppercase text-[#8A8F98]">Created by</div>
                <div className="text-sm font-heading text-[#F8F9FA] mt-0.5" data-testid="history-created-by">{data.campaign.created_by_name || "—"}</div>
                <div className="text-[10px] text-[#8A8F98]">{data.campaign.created_at ? formatDate(data.campaign.created_at) : ""}</div>
              </div>
              <div className="p-3 bg-[#0B0F0C] border border-[#3A4640] rounded">
                <div className="text-[10px] uppercase text-[#8A8F98]">Assigned to</div>
                <div className="text-sm font-heading text-[#F8F9FA] mt-0.5">
                  {(data.campaign.assigned_to_names || []).join(", ") || "—"}
                </div>
              </div>
              <div className="p-3 bg-[#0B0F0C] border border-[#3A4640] rounded">
                <div className="text-[10px] uppercase text-[#8A8F98]">Contacted</div>
                <div className="text-2xl font-heading font-bold text-[#F8F9FA]" data-testid="history-contacted-count">{data.contacted_count}</div>
                <div className="text-[10px] text-[#8A8F98]">of {data.campaign.total}</div>
              </div>
              <div className="p-3 bg-[#0B0F0C] border border-[#3A4640] rounded">
                <div className="text-[10px] uppercase text-[#8A8F98]">Interactions</div>
                <div className="text-2xl font-heading font-bold text-[#F8F9FA]">{data.interaction_count}</div>
              </div>
            </div>

            <div className="p-3 bg-[#0B0F0C] border border-[#3A4640] rounded">
              <div className="text-[10px] uppercase text-[#8A8F98] mb-2">Actual callers</div>
              <div className="flex flex-wrap gap-2" data-testid="history-actual-callers">
                {data.actual_callers?.length ? data.actual_callers.map((c) => (
                  <span key={c.name} className="text-xs px-2 py-1 rounded border border-[#3A4640] bg-[#121815] text-[#F8F9FA]">
                    {c.name} <span className="text-[#8A8F98]">· {c.count}</span>
                  </span>
                )) : <span className="text-xs text-[#8A8F98]">No calls yet</span>}
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase text-[#8A8F98] mb-2">Interactions</div>
              <div className="border border-[#3A4640] rounded max-h-80 overflow-y-auto" data-testid="history-interactions-list">
                {(data.interactions || []).length === 0 && (
                  <div className="p-4 text-center text-xs text-[#8A8F98]">No interactions recorded yet.</div>
                )}
                {(data.interactions || []).map((it) => (
                  <div key={it.id} className="px-3 py-2 border-b border-[#3A4640] last:border-0 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-heading font-semibold text-[#F8F9FA] truncate">
                        {it.customer?.name || "—"} <span className="text-[#8A8F98] font-mono text-[10px]">{it.customer?.tzat_no}</span>
                      </div>
                      <div className="text-[10px] text-[#8A8F98] whitespace-nowrap">{formatDate(it.called_at)}</div>
                    </div>
                    <div className="text-xs text-[#8A8F98]">
                      {it.outcome_label} · by {it.user_name || "—"}
                      {it.machine_assessments?.length ? ` · ${it.machine_assessments.length} machine assessment(s)` : ""}
                    </div>
                    {it.note && <div className="text-xs text-[#F8F9FA] italic mt-0.5 truncate">"{it.note}"</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// -------------------- Campaign Results Modal --------------------
function DrillBucket({ title, buckets, testid }) {
  const [open, setOpen] = useState(null);
  if (!buckets?.length) return (
    <div className="p-3 bg-[#0B0F0C] border border-[#3A4640] rounded">
      <div className="text-[10px] uppercase text-[#8A8F98] mb-1">{title}</div>
      <div className="text-xs text-[#8A8F98]">No data captured yet.</div>
    </div>
  );
  return (
    <div className="p-3 bg-[#0B0F0C] border border-[#3A4640] rounded" data-testid={testid}>
      <div className="text-[10px] uppercase text-[#8A8F98] mb-2">{title}</div>
      <div className="space-y-1">
        {buckets.map((b) => {
          const isOpen = open === b.key;
          return (
            <div key={b.key}>
              <button onClick={() => setOpen(isOpen ? null : b.key)}
                data-testid={`${testid}-bucket-${b.key}`}
                className="w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-[#121815] text-left text-sm">
                <span className="text-[#F8F9FA]">{_lbl(b.key)}</span>
                <span className="text-[#39FF14] font-heading font-bold">{b.count}</span>
              </button>
              {isOpen && b.customer_ids?.length > 0 && (
                <div className="ml-2 my-1 pl-2 border-l border-[#3A4640] text-[11px] text-[#8A8F98] max-h-40 overflow-y-auto" data-testid={`${testid}-drill-${b.key}`}>
                  {b.customer_ids.slice(0, 200).map((cid) => (
                    <div key={cid}><Link className="hover:text-[#39FF14]" to={`/customers/${cid}`} target="_blank" rel="noopener">{cid.slice(0, 8)}…</Link></div>
                  ))}
                  {b.customer_ids.length > 200 && <div className="italic">…{b.customer_ids.length - 200} more</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CampaignResultsModal({ campaign, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!campaign) { setData(null); return; }
    setLoading(true);
    api.get(`/calling/campaigns/${campaign.id}/results`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load results"))
      .finally(() => setLoading(false));
  }, [campaign]);

  return (
    <Dialog open={!!campaign} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="campaign-results-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BarChart3 size={16} /> Campaign Results</DialogTitle>
          <DialogDescription>{campaign?.name} — click any row to drill down</DialogDescription>
        </DialogHeader>
        {loading && <div className="p-6 text-center"><Loader2 size={20} className="animate-spin mx-auto text-[#8A8F98]" /></div>}
        {data && (
          <div className="grid md:grid-cols-2 gap-3 py-2">
            <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-[#0B0F0C] border border-[#39FF14]/25 rounded"><div className="text-[10px] text-[#8A8F98] uppercase">Contacted</div><div className="text-2xl font-heading font-bold text-[#39FF14]" data-testid="results-contacted">{data.contacted}</div></div>
              <div className="p-3 bg-[#0B0F0C] border border-[#3A4640] rounded"><div className="text-[10px] text-[#8A8F98] uppercase">Interactions</div><div className="text-2xl font-heading font-bold text-[#F8F9FA]">{data.interactions}</div></div>
              <div className="p-3 bg-[#0B0F0C] border border-[#3A4640] rounded"><div className="text-[10px] text-[#8A8F98] uppercase">Machines Assessed</div><div className="text-2xl font-heading font-bold text-[#F8F9FA]" data-testid="results-machines-assessed">{data.machines_assessed}<span className="text-sm text-[#8A8F98]"> / {data.machines_total}</span></div></div>
              <div className="p-3 bg-[#0B0F0C] border border-[#3A4640] rounded"><div className="text-[10px] text-[#8A8F98] uppercase">Total in list</div><div className="text-2xl font-heading font-bold text-[#F8F9FA]">{data.campaign.total}</div></div>
            </div>
            <DrillBucket title="Contact Result"        buckets={data.contact_result}         testid="results-contact-result" />
            <DrillBucket title="Interest Level"        buckets={data.interest_level}         testid="results-interest-level" />
            <DrillBucket title="Purchase Timeframe"    buckets={data.purchase_timeframe}     testid="results-timeframe" />
            <DrillBucket title="Category Interest"     buckets={data.new_machine_categories} testid="results-categories" />
            <DrillBucket title="Machine Conditions"    buckets={data.machine_conditions}     testid="results-conditions" />
            <DrillBucket title="Service Requirements"  buckets={data.service_requirements}   testid="results-services" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CampaignBuilder({ open, onClose, districts, states, categories, brands, locationTree, onCreated }) {
  const [name, setName] = useState("");
  const [customerTypes, setCustomerTypes] = useState([]);
  const [chosenDistricts, setChosenDistricts] = useState([]);
  const [chosenBlocks, setChosenBlocks] = useState([]);
  const [chosenGPs, setChosenGPs] = useState([]);
  const [chosenVillages, setChosenVillages] = useState([]);
  const [chosenStatuses, setChosenStatuses] = useState(["active"]);
  const [chosenCategories, setChosenCategories] = useState([]);
  const [chosenBrands, setChosenBrands] = useState([]);
  const [incomplete, setIncomplete] = useState("any");
  const [neverContacted, setNeverContacted] = useState(false);
  const [notContactedDays, setNotContactedDays] = useState("");
  const [previousContactWarningDays, setPreviousContactWarningDays] = useState(30);
  const [includeOptedOut, setIncludeOptedOut] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [locFilter, setLocFilter] = useState("");

  useEffect(() => {
    if (!open) {
      setName(""); setCustomerTypes([]);
      setChosenDistricts([]); setChosenBlocks([]); setChosenGPs([]); setChosenVillages([]);
      setChosenStatuses(["active"]); setChosenCategories([]); setChosenBrands([]);
      setIncomplete("any"); setNeverContacted(false); setNotContactedDays("");
      setPreviousContactWarningDays(30);
      setIncludeOptedOut(false); setPreview(null); setLocFilter("");
    }
  }, [open]);

  const toggle = (list, setter, value) => {
    if (list.includes(value)) setter(list.filter((v) => v !== value));
    else setter([...list, value]);
  };

  // ---- Location cascade: filter each level to descendants of the level above ----
  const availableBlocks = useMemo(() => {
    const scope = chosenDistricts.length
      ? locationTree.filter((d) => chosenDistricts.includes(d.name))
      : locationTree;
    const seen = new Set();
    const out = [];
    scope.forEach((d) => (d.blocks || []).forEach((b) => {
      if (b.name && !seen.has(b.name)) { seen.add(b.name); out.push(b); }
    }));
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [locationTree, chosenDistricts]);

  const availableGPs = useMemo(() => {
    const scope = chosenDistricts.length
      ? locationTree.filter((d) => chosenDistricts.includes(d.name))
      : locationTree;
    const seen = new Set();
    const out = [];
    scope.forEach((d) => (d.blocks || []).forEach((b) => {
      if (chosenBlocks.length && !chosenBlocks.includes(b.name)) return;
      (b.gram_panchayats || []).forEach((gp) => {
        if (gp.name && !seen.has(gp.name)) { seen.add(gp.name); out.push(gp); }
      });
    }));
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [locationTree, chosenDistricts, chosenBlocks]);

  const availableVillages = useMemo(() => {
    const scope = chosenDistricts.length
      ? locationTree.filter((d) => chosenDistricts.includes(d.name))
      : locationTree;
    const seen = new Set();
    const out = [];
    scope.forEach((d) => (d.blocks || []).forEach((b) => {
      if (chosenBlocks.length && !chosenBlocks.includes(b.name)) return;
      (b.gram_panchayats || []).forEach((gp) => {
        if (chosenGPs.length && !chosenGPs.includes(gp.name)) return;
        (gp.villages || []).forEach((v) => {
          if (v.name && !seen.has(v.name)) { seen.add(v.name); out.push(v); }
        });
      });
    }));
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [locationTree, chosenDistricts, chosenBlocks, chosenGPs]);

  // Prune child selections when a parent is unchecked
  useEffect(() => {
    const validBlocks = new Set(availableBlocks.map((b) => b.name));
    setChosenBlocks((prev) => prev.filter((b) => validBlocks.has(b)));
  }, [availableBlocks]);
  useEffect(() => {
    const validGPs = new Set(availableGPs.map((g) => g.name));
    setChosenGPs((prev) => prev.filter((g) => validGPs.has(g)));
  }, [availableGPs]);
  useEffect(() => {
    const validVillages = new Set(availableVillages.map((v) => v.name));
    setChosenVillages((prev) => prev.filter((v) => validVillages.has(v)));
  }, [availableVillages]);

  const buildFilters = () => ({
    customer_types: customerTypes,
    districts:      chosenDistricts,
    blocks:         chosenBlocks,
    gram_panchayats: chosenGPs,
    villages:       chosenVillages,
    states:         [],
    statuses:       chosenStatuses,
    product_categories: chosenCategories,
    brands:         chosenBrands,
    incomplete:     incomplete === "any" ? null : incomplete === "incomplete",
    never_contacted: neverContacted,
    not_contacted_days: notContactedDays ? parseInt(notContactedDays, 10) : null,
    include_opted_out: includeOptedOut,
  });

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/calling/campaigns/preview", buildFilters());
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Preview failed"); }
    finally { setPreviewing(false); }
  };

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/calling/campaigns", {
        name: name.trim() || `Campaign · ${new Date().toLocaleString()}`,
        filters: buildFilters(),
      });
      // Persist per-campaign warning days via the assignments PATCH.
      const days = parseInt(previousContactWarningDays, 10);
      if (Number.isFinite(days) && days > 0) {
        try {
          await api.patch(`/calling/campaigns/${data.id}/assignments`, {
            previous_contact_warning_days: days,
          });
        } catch (_) { /* non-fatal — campaign still created */ }
      }
      toast.success(`Campaign created — ${data.total} customers`);
      onCreated(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to create campaign"); }
    finally { setCreating(false); }
  };

  const Chip = ({ active, onClick, children, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors font-heading ${active ? "bg-[#39FF14] text-[#0B0F0C] border-[#39FF14]/40" : "bg-[#121815] text-[#F8F9FA] border-[#3A4640] hover:bg-[#121815]"}`}>
      {children}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="campaign-builder">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Filter size={16} /> New Calling Campaign</DialogTitle>
          <DialogDescription>Filter your existing customers into a calling queue. Interactions are logged as you go.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <Label>Campaign Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Post-monsoon service outreach" data-testid="campaign-name-input" />
          </div>

          <div>
            <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] mb-1.5">Customer Type</div>
            <div className="flex flex-wrap gap-2">
              {["individual", "business", "government"].map((t) => (
                <Chip key={t} active={customerTypes.includes(t)} onClick={() => toggle(customerTypes, setCustomerTypes, t)} testid={`filter-type-${t}`}>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${TYPE_DOT[t]}`} />
                  {TYPE_LABEL[t]}
                </Chip>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] mb-1.5">Status</div>
            <div className="flex flex-wrap gap-2">
              {["active", "inactive", "archived"].map((s) => (
                <Chip key={s} active={chosenStatuses.includes(s)} onClick={() => toggle(chosenStatuses, setChosenStatuses, s)} testid={`filter-status-${s}`}>{s}</Chip>
              ))}
            </div>
          </div>

          {locationTree.length > 0 && (
            <div className="border border-[#3A4640] rounded-md">
              <div className="flex items-center justify-between px-3 py-2 border-b border-[#3A4640] bg-[#0B0F0C]">
                <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98]">
                  Location · {chosenDistricts.length}D · {chosenBlocks.length}B · {chosenGPs.length}GP · {chosenVillages.length}V
                </div>
                {(chosenDistricts.length || chosenBlocks.length || chosenGPs.length || chosenVillages.length) > 0 && (
                  <button
                    type="button"
                    className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] hover:text-red-600"
                    onClick={() => { setChosenDistricts([]); setChosenBlocks([]); setChosenGPs([]); setChosenVillages([]); }}
                    data-testid="clear-locations-btn"
                  >Clear</button>
                )}
              </div>
              <div className="px-3 py-2 border-b border-[#3A4640]">
                <Input
                  placeholder="Filter locations…"
                  value={locFilter}
                  onChange={(e) => setLocFilter(e.target.value)}
                  data-testid="location-filter-input"
                  className="h-8 text-sm"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[#3A4640]">
                {[
                  { title: "District", items: locationTree,        chosen: chosenDistricts, setter: setChosenDistricts, testkey: "district" },
                  { title: "Block",    items: availableBlocks,     chosen: chosenBlocks,    setter: setChosenBlocks,    testkey: "block" },
                  { title: "Panchayat",items: availableGPs,        chosen: chosenGPs,       setter: setChosenGPs,       testkey: "gp" },
                  { title: "Village",  items: availableVillages,   chosen: chosenVillages,  setter: setChosenVillages,  testkey: "village" },
                ].map((col) => {
                  const filtered = (col.items || []).filter((it) =>
                    !locFilter || (it.name || "").toLowerCase().includes(locFilter.toLowerCase())
                  );
                  return (
                    <div key={col.title} className="min-w-0" data-testid={`loc-col-${col.testkey}`}>
                      <div className="px-3 py-1.5 text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] bg-[#0B0F0C]/50 border-b border-[#3A4640]">
                        {col.title} <span className="text-[#8A8F98] normal-case">({filtered.length})</span>
                      </div>
                      <div className="max-h-52 overflow-y-auto">
                        {filtered.length === 0 && (
                          <div className="px-3 py-3 text-xs text-[#8A8F98] italic">
                            {col.items.length === 0 ? "Select above to see options" : "No matches"}
                          </div>
                        )}
                        {filtered.map((it) => {
                          const isOn = col.chosen.includes(it.name);
                          return (
                            <label
                              key={it.name}
                              className={`flex items-center justify-between gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-[#0B0F0C] ${isOn ? "bg-[#39FF14]/5/80" : ""}`}
                              data-testid={`loc-${col.testkey}-${it.name}`}
                            >
                              <span className="flex items-center gap-2 min-w-0">
                                <input
                                  type="checkbox"
                                  checked={isOn}
                                  onChange={() => toggle(col.chosen, col.setter, it.name)}
                                  className="shrink-0"
                                />
                                <span className="truncate">{it.name}</span>
                              </span>
                              {typeof it.count === "number" && (
                                <span className="text-[10px] font-mono text-[#8A8F98] shrink-0">{it.count}</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-3 py-1.5 text-[10px] font-heading text-[#8A8F98] bg-[#0B0F0C]/60 border-t border-[#3A4640]">
                Selecting a higher level includes all its descendants automatically. Refine by ticking lower levels.
              </div>
            </div>
          )}

          {categories.length > 0 && (
            <div>
              <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] mb-1.5">Machine Category ({chosenCategories.length || "any"})</div>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {categories.map((c) => (
                  <Chip key={c} active={chosenCategories.includes(c)} onClick={() => toggle(chosenCategories, setChosenCategories, c)} testid={`filter-cat-${c}`}>{c}</Chip>
                ))}
              </div>
            </div>
          )}

          {brands.length > 0 && (
            <div>
              <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] mb-1.5">Brand ({chosenBrands.length || "any"})</div>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {brands.map((b) => (
                  <Chip key={b} active={chosenBrands.includes(b)} onClick={() => toggle(chosenBrands, setChosenBrands, b)} testid={`filter-brand-${b}`}>{b}</Chip>
                ))}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Data Quality</Label>
              <Select value={incomplete} onValueChange={setIncomplete}>
                <SelectTrigger data-testid="filter-incomplete"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="complete">Complete only</SelectItem>
                  <SelectItem value="incomplete">Incomplete only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end pb-1">
              <label className="text-sm text-[#F8F9FA] flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={neverContacted} onChange={(e) => setNeverContacted(e.target.checked)} data-testid="filter-never-contacted" />
                Never contacted
              </label>
            </div>
            <div>
              <Label>Not contacted in last (days)</Label>
              <Input type="number" min={1} value={notContactedDays} onChange={(e) => setNotContactedDays(e.target.value)} placeholder="e.g. 90" data-testid="filter-not-days" />
            </div>
          </div>

          <div>
            <label className="text-sm text-[#F8F9FA] flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={includeOptedOut} onChange={(e) => setIncludeOptedOut(e.target.checked)} data-testid="filter-include-optout" />
              Include opted-out customers (not recommended — respects DND by default)
            </label>
          </div>

          <div className="p-3 bg-[#0B0F0C] border border-[#39FF14]/20 rounded-md">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase font-heading tracking-wider text-[#39FF14]">Previous-Contact Warning</div>
                <div className="text-xs text-[#8A8F98] mt-1">Show a caller a soft warning banner if this customer was already contacted within the last N days. Informational only — never blocks the call.</div>
              </div>
              <div className="shrink-0 w-32">
                <Label className="text-[11px]">Days</Label>
                <Input type="number" min={0} value={previousContactWarningDays}
                  onChange={(e) => setPreviousContactWarningDays(e.target.value)}
                  placeholder="30" data-testid="prev-contact-warning-days-input" />
              </div>
            </div>
          </div>

          <div className="p-3 bg-[#0B0F0C] border border-[#3A4640] rounded-md flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98]">Preview</div>
              <div className="text-lg font-heading font-bold text-[#F8F9FA]" data-testid="preview-total">
                {preview ? `${preview.total} customers match` : "Not previewed yet"}
              </div>
              {preview?.sample?.length > 0 && (
                <div className="text-xs text-[#8A8F98] mt-1 truncate max-w-md">
                  e.g. {preview.sample.map((s) => s.name || s.organization_name || s.department_name).filter(Boolean).slice(0, 3).join(", ")}{preview.total > 3 && "…"}
                </div>
              )}
            </div>
            <Button variant="outline" onClick={runPreview} disabled={previewing} data-testid="preview-btn">
              {previewing ? <Loader2 size={14} className="animate-spin mr-2" /> : <Users size={14} className="mr-2" />}
              Count matches
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#39FF14] hover:bg-[#2ECC71]" onClick={create} disabled={creating || (preview && preview.total === 0)} data-testid="create-campaign-submit-btn">
            {creating ? <Loader2 size={14} className="animate-spin mr-2" /> : <PhoneCall size={14} className="mr-2" />}
            Create & Start
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
